import React from "react";
import { Box, Flex, Text, Tooltip } from "@chakra-ui/react";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useSettlementManagementQuery from "@/features/my-order/features/settlement-management/hooks/useSettlementManagementQuery";
import { UnoFee } from "@/features/my-order/features/settlement-management/types";
import useDownloadUnoFile from "@/hooks/use-download-uno-file";
import { formatDateToYMDEGeneral } from "@/shared/utils";
import useRouterHelper from "@/features/ui/hooks/use-router-helper";

interface Props {
  unoFee: UnoFee;
}

function SettlementManagementIBillingDetail({ unoFee }: Props) {
  const { tTitle, tSentence, isKor } = useI18nHelper();
  const { setQuery } = useSettlementManagementQuery();
  const { fire: fireDownloadUnoFile } = useDownloadUnoFile();
  const { calcNavigateTo } = useRouterHelper();

  const {
    orderSheetSets,
    proformaInvoice,
    finalCustomsClearanceDate,
    settlementMonth,
    isDelayed,
    invoiceAmount,
    exchangeRate,
    unoFeeRate,
    unoFeeAmount,
  } = unoFee ?? {};

  // unoFeeAmount는 부가세 10% 포함 금액이므로 공급가액/부가세를 역산해 표시한다.
  const unoFeeSupplyAmount =
    unoFeeAmount != null ? Math.round(unoFeeAmount / 1.1) : null;
  const unoFeeVatAmount =
    unoFeeAmount != null && unoFeeSupplyAmount != null
      ? unoFeeAmount - unoFeeSupplyAmount
      : null;

  const billingDetailSummaryList = [
    {
      title: "order_history",
      value: (
        <>
          {orderSheetSets.map((oss) => (
            <Tooltip
              key={oss.id}
              label={tTitle("settlement:fee_tooltip.open_order_with_new_tab")}
            >
              <Flex
                gap={"4px"}
                alignItems={"center"}
                className={"group"}
                cursor={"pointer"}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(
                    calcNavigateTo(
                      `/order-sheet-sets/order-history?order_sheet_set_id=${oss.id}`
                    ),
                    "_blank"
                  );
                }}
              >
                <Text
                  textStyle={"body-md-bold"}
                  color={"#444444"}
                  noOfLines={1}
                  wordBreak={"break-all"}
                  width={"90%"}
                  _groupHover={{
                    textDecoration: "underline",
                    color: "#1272EF",
                  }}
                  _groupActive={{
                    color: "#00417D",
                  }}
                >
                  {`#${oss.lineSheetSet.id} ${oss.lineSheetSet.name}`}
                </Text>
                <Box
                  color={"#8F8F8C"}
                  _groupHover={{
                    color: "#1272EF",
                  }}
                  _groupActive={{
                    color: "#00417D",
                  }}
                >
                  <CustomIcon src={"arrow-square"} width={20} height={20} />
                </Box>
              </Flex>
            </Tooltip>
          ))}
        </>
      ),
    },
    {
      title: "invoice",
      value: (
        <Flex gap={"4px"}>
          <Text textStyle={"body-md-medium"} color={"#363636"}>
            {proformaInvoice.name ?? "-"}
          </Text>
          <Tooltip label={tTitle("download_document")}>
            <Flex
              color={"#8F8F8C"}
              _hover={{
                color: "#1272EF",
              }}
              _active={{
                color: "#00417D",
              }}
              cursor={"pointer"}
              onClick={() => {
                fireDownloadUnoFile(
                  {
                    unoFileId: proformaInvoice.file.id,
                  },
                  {}
                );
              }}
              justifyContent={"center"}
              alignItems={"center"}
            >
              <CustomIcon src={"document-down"} width={20} height={20} />
            </Flex>
          </Tooltip>
        </Flex>
      ),
    },
    {
      title: "final_customs_date",
      value: (
        <Text textStyle={"body-md-medium"} color={"#363636"}>
          {formatDateToYMDEGeneral(finalCustomsClearanceDate ?? "", true, "-")}
        </Text>
      ),
    },
    {
      title: "billing_month",
      value: (
        <Flex gap={"8px"}>
          <Text textStyle={"body-md-medium"} color={"#363636"}>
            {settlementMonth}
          </Text>
          {isDelayed && (
            <Flex>
              <Flex gap={"4px"} alignItems={"center"}>
                <CustomIcon
                  src={"warning-circle-solid"}
                  color={"#E5B400"}
                  width={16}
                  height={16}
                />
                <Text color={"#B27700"} textStyle={"caption-regular"}>
                  {tSentence("settlement:carry_over_warning")}
                </Text>
              </Flex>
            </Flex>
          )}
        </Flex>
      ),
    },
  ];

  return (
    <Flex
      padding={"16px"}
      flexDirection={"column"}
      gap={"16px"}
      border={"1px solid #D4D4D1"}
      borderRadius={"8px"}
      height={"100%"}
    >
      <Flex justifyContent={"space-between"}>
        <Text textStyle={"body-lg-bold"}>
          {tTitle("settlement:billing_detail")}
        </Text>
        <Tooltip
          label={tTitle("settlement:fee_tooltip.close_detail")}
          placement={"top"}
        >
          <Box
            color={"#8F8F8C"}
            _hover={{
              color: "#1272EF",
            }}
            _active={{
              color: "#00417D",
            }}
          >
            <CustomIcon
              src={"x"}
              width={24}
              height={24}
              onClick={() => {
                setQuery((prev) => ({
                  ...prev,
                  unoFeeId: undefined,
                }));
              }}
            />
          </Box>
        </Tooltip>
      </Flex>
      <Flex flexDirection={"column"} gap={"8px"}>
        {billingDetailSummaryList.map((item, index) => (
          <Flex key={index} gap={"6px"} width={"100%"} alignItems={"center"}>
            <Text
              textStyle={"body-sm-medium"}
              color={"#6F6F6C"}
              width={"80px"}
              whiteSpace={"nowrap"}
            >
              {tTitle(`settlement:${item.title}`)}
            </Text>
            <Box flex={1} width={"80%"}>
              {item.value}
            </Box>
          </Flex>
        ))}
        <Flex
          flexDirection={"column"}
          borderRadius={"8px"}
          boxShadow={"0 0 0 0.5px #D4D4D1"}
          padding={"16px"}
          gap={"8px"}
        >
          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            paddingX={"8px"}
            textStyle={"body-sm-medium"}
            color={"#444444"}
          >
            <Text>{tTitle(`settlement:category`)}</Text>
            <Text>{tTitle(`amount`)}</Text>
          </Flex>
          <Flex
            flexDirection={"column"}
            textStyle={"body-sm-regular"}
            color={"#6F6F6C"}
          >
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              paddingX={"8px"}
            >
              <Text>{tTitle("invoice_amount")}</Text>
              <Text textStyle={"body-md-regular"} color={"#444444"}>
                €{invoiceAmount.toLocaleString()}
              </Text>
            </Flex>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              paddingX={"8px"}
            >
              <Text>{tTitle(`exchange_rate`)}</Text>
              <Text textStyle={"body-md-regular"} color={"#444444"}>
                ₩{exchangeRate.toLocaleString()}
              </Text>
            </Flex>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              paddingX={"8px"}
            >
              <Text>{tTitle("settlement:fee_rate")}</Text>
              <Text textStyle={"body-md-regular"} color={"#444444"}>
                {unoFeeRate * 100}%
              </Text>
            </Flex>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              paddingX={"8px"}
            >
              <Text>{isKor ? "수수료 (공급가액)" : "Fee (excl. VAT)"}</Text>
              <Text textStyle={"body-md-regular"} color={"#444444"}>
                {unoFeeSupplyAmount != null
                  ? "₩" + unoFeeSupplyAmount.toLocaleString()
                  : "-"}
              </Text>
            </Flex>
            <Flex
              justifyContent={"space-between"}
              alignItems={"center"}
              paddingX={"8px"}
            >
              <Text>{isKor ? "부가세 (10%)" : "VAT (10%)"}</Text>
              <Text textStyle={"body-md-regular"} color={"#444444"}>
                {unoFeeVatAmount != null
                  ? "₩" + unoFeeVatAmount.toLocaleString()
                  : "-"}
              </Text>
            </Flex>
          </Flex>
          <Flex
            justifyContent={"space-between"}
            alignItems={"center"}
            padding={"10px 8px"}
            backgroundColor={"#FFF7CC"}
            borderTop={"1px solid #D4D4D1"}
          >
            <Text textStyle={"body-sm-medium"} color={"#444444"}>
              {tTitle("settlement:total_billing_amount")}
            </Text>
            <Text textStyle={"body-md-semi-bold"} color={"#1272EF"}>
              ₩{unoFeeAmount.toLocaleString()}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
}

export default SettlementManagementIBillingDetail;
